import { Navigate, redirect } from "react-router-dom";

import Login from 'pages/Login';
import ToLogin from 'pages/ToLogin';
import Impersonate from 'pages/Impersonate';
import Protected from 'Protected';
import ProfileSettings from 'pages/ProfileSettings';
import About from 'pages/About';
import NotFound from 'pages/NotFound';

import InitialSetup from 'pages/InitialSetup/Index';
import InitialSetupUsers from 'pages/InitialSetup/Users';
import InitialSetupRoles from 'pages/InitialSetup/Roles';
import InitialImportSources from "pages/InitialSetup/ImportSources";

import EmployeeManagementNew from 'pages/EmployeeManagement/New';

import EvaluationStatistics from 'pages/EvaluationStatistics/Index';
import EvaluationStatisticsCockpit from 'pages/EvaluationStatistics/Cockpit';
import EvaluationStatisticsAssessmentMatrix from 'pages/EvaluationStatistics/AssessmentMatrix';
import EvaluationStatisticsEvaluationTrends from 'pages/EvaluationStatistics/EvaluationTrends';
import EvaluationStatisticsTargetMonitoring from 'pages/EvaluationStatistics/TargetMonitoring';
import EvaluationStatisticsInterviewStatus from 'pages/EvaluationStatistics/InterviewStatus';
import EvaluationStatisticsEvaluatorMonitoring from 'pages/EvaluationStatistics/EvaluatorMonitoring';
import EvaluationStatisticsGroupBudgetAnalysis from 'pages/EvaluationStatistics/GroupBudgetAnalysis';
import EvaluationStatisticsRiskFactorsAnomalies from 'pages/EvaluationStatistics/RiskFactorsAnomalies';
import EvaluationStatisticsStrategicTargetAlignment from 'pages/EvaluationStatistics/StrategicTargetAlignment';
import EvaluationStatisticsSystemUsageActivities from 'pages/EvaluationStatistics/SystemUsageActivities';

import Evaluation from 'pages/Evaluation/Index';
import EvaluationEvaluatorOverview from 'pages/Evaluation/EvaluatorOverview';
import EvaluationSecondaryEvaluatorOverview from 'pages/Evaluation/DB/SecondaryEvaluatorOverview';
import EvaluationReview from 'pages/Evaluation/Review';
import EvaluationAllEvaluations from 'pages/Evaluation/AllEvaluations';
import EvaluationTeamObjectivesOverview from 'pages/Evaluation/TeamObjectivesOverview';

import Calculation from 'pages/Calculation/Index';

import Settings from 'pages/Settings/Index';
import SettingsImport from 'pages/Settings/Imports/Import';
import SettingsEmployeeImportDetail from 'pages/Settings/Imports/EmployeeImportDetail';
import SettingsFieldConfiguration from 'pages/Settings/FieldConfiguration';
import SettingsBalanceScorecard from 'pages/Settings/Scorecard/BalanceScorecard';
import SettingsBalanceScorecardForm from 'pages/Settings/Scorecard/BalanceScorecardForm';
import SettingsCalculation from 'pages/Settings/Calculation';
import SettingsCalculationForm from 'pages/Settings/CalculationForm';
import SettingsBudgets from 'pages/Settings/Budgets/Budgets';
import SettingsUserImportDetail from 'pages/Settings/Imports/UserImportDetail';
import SettingsFilters from 'pages/Settings/Filters';
import SettingsRoles from 'pages/Settings/Roles';
import SettingsUsers from 'pages/Settings/Users';
import SettingsFreeFields from 'pages/Settings/FreeFields';
import SettingsDisplayOptions from 'pages/Settings/DisplayOptions';
import SettingsPayFactors from 'pages/Settings/PayFactors';
import SettingsInterview from 'pages/Settings/Interview';
import SettingsGeneral from 'pages/Settings/General';
import SettingsEmail from 'pages/Settings/Email';
import SettingsReport from 'pages/Settings/Report';
import SettingGroups from 'pages/Settings/Groups';
import SettingAuditLogs from 'pages/Settings/AuditLogs';
import PeriodManagement from 'pages/Settings/PeriodManagement';
import SettingsAction from 'pages/Settings/Actions/Action';
import SettingsActionDetail from 'pages/Settings/Actions/ActionDetail';

import Help from 'pages/Help/Index';

import { isUserCookie } from 'helpers/cookie';
import PreliminaryCheck from "pages/Calculation/PreliminaryCheck";
import CalculationDebugging from "pages/Calculation/CalculationDebugging";
import CalculationTest from "pages/Calculation/CalculationTest";
import Results from "pages/Calculation/Results";
import PeriodSelector from "pages/PeriodSelector";

const router = [
  {
    path: "/login/:accountId?",
    element: <Login />,
    loader: async (req) => {
      if (isUserCookie() && !req.params.accountId) {
        return redirect('/employee-management');
      }
      return true;
    },
  },
  {
    path: "/to-login",
    element: <ToLogin />
  },
  {
    path: "/impersonate",
    element: <Impersonate />
  },
  {
    path: "/",
    element: <Navigate to="/employee-management" replace />,
  },
  {
    path: "/period-selector",
    element: <PeriodSelector />,
    loader: async () => {
      if (!isUserCookie()) return redirect('/to-login');
      return true;
    }
  },
  {
    path: "/:module?",
    element: <Protected />,
    loader: async () => {
      if (!isUserCookie()) return redirect('/to-login');
      return true;
    },
    shouldRevalidate: () => true,
    children: [
      {
        key: "employee-management",
        path: "employee-management",
        element: <EmployeeManagementNew />
      },
      {
        path: "evaluation-statistics",
        element: <EvaluationStatistics />,
        children: [
          {
            path: "cockpit",
            element: <EvaluationStatisticsCockpit />
          },
          {
            path: "assessment-matrix",
            element: <EvaluationStatisticsAssessmentMatrix />
          },
          {
            path: "evaluation-trends",
            element: <EvaluationStatisticsEvaluationTrends />
          },
          {
            path: "target-monitoring",
            element: <EvaluationStatisticsTargetMonitoring />
          },
          {
            path: "interview-status",
            element: <EvaluationStatisticsInterviewStatus />
          },
          {
            path: "evaluator-monitoring",
            element: <EvaluationStatisticsEvaluatorMonitoring />
          },
          {
            path: "group-budget-analysis",
            element: <EvaluationStatisticsGroupBudgetAnalysis />
          },
          {
            path: "risk-factors-anomalies",
            element: <EvaluationStatisticsRiskFactorsAnomalies />
          },
          {
            path: "strategic-target-alignment",
            element: <EvaluationStatisticsStrategicTargetAlignment />
          },
          {
            path: "system-usage-activities",
            element: <EvaluationStatisticsSystemUsageActivities />
          },
        ]
      },
      {
        path: "evaluation",
        element: <Evaluation />,
        children: [
          {
            path: "evaluator-overview",
            element: <EvaluationEvaluatorOverview />
          },
          {
            path: "secondary-evaluator-overview",
            element: <EvaluationSecondaryEvaluatorOverview />
          },
          {
            path: "review",
            element: <EvaluationReview />
          },
          {
            path: "all-evaluations",
            element: <EvaluationAllEvaluations />
          },
          {
            key: "team-objectives-overview",
            path: "team-objectives-overview",
            element: <EvaluationTeamObjectivesOverview />
          }
        ]
      },
      {
        path: "calculation",
        element: <Calculation />,
        children: [
          {
            key: "calculation-overview",
            path: "preliminary-check",
            element: <PreliminaryCheck />
          },
          {
            path: "results",
            element: <Results />
          },
          {
            path: "calculation-debugging",
            element: <CalculationDebugging />
          },
          {
            path: "calculation-test",
            element: <CalculationTest />
          }
        ]
      },
      {
        path: "settings",
        element: <Settings />,
        children: [
          {
            path: "balance-scorecard",
            element: <SettingsBalanceScorecard />
          },
          {
            key: "balanced-scorecard-setting-form",
            path: "balance-scorecard/form/:id?",
            element: <SettingsBalanceScorecardForm />
          },
          {
            path: "calculation",
            testKey: "calculation-settings",
            element: <SettingsCalculation />
          },
          {
            key: "calculation-setting-form",
            path: "calculation/form/:id?",
            element: <SettingsCalculationForm />
          },
          {
            key: "budgets",
            path: "budgets",
            element: <SettingsBudgets />
          },
          {
            key: "filters",
            path: "filters",
            element: <SettingsFilters />
          },
          {
            path: "roles",
            element: <Navigate to="/system-settings/roles" replace />
          },
          {
            path: "free-fields",
            element: <SettingsFreeFields />
          },
          {
            path: "display-options",
            element: <SettingsDisplayOptions />
          },
          {
            key: "interview",
            path: "interview",
            element: <SettingsInterview />
          },
          {
            path: "general",
            element: <SettingsGeneral />
          },
          {
            path: "report",
            element: <SettingsReport />
          },
          {
            key: "groups-settings",
            path: "groups",
            element: <SettingGroups />
          },
          {
            key: "actions",
            path: "actions",
            element: <SettingsAction />
          },
          {
            path: "actions/suggested/:hashId",
            testKey: "action-suggested",
            element: <SettingsActionDetail variant="suggested" />
          },
          {
            path: "actions/system-filter/:hashId",
            testKey: "action-system-filter",
            element: <SettingsActionDetail variant="system-filter" />
          },
          {
            path: "actions/custom",
            element: <SettingsActionDetail variant="custom" />
          }
        ]
      },
      {
        path: "system-settings",
        element: <Settings />,
        children: [
          {
            key: "import-settings",
            path: "import",
            element: <SettingsImport />
          },
          {
            path: "users",
            element: <SettingsUsers />
          },
          {
            path: "roles",
            element: <SettingsRoles />
          },
          {
            path: "import/employees/:id",
            testKey: "employee-import-detail",
            element: <SettingsEmployeeImportDetail />
          },
          {
            path: "import/login-users/:id",
            testKey: "login-user-import-detail",
            element: <SettingsUserImportDetail />
          },
          {
            key: "field-configuration",
            path: "field-configuration",
            element: <SettingsFieldConfiguration />
          },
          {
            path: "pay-factors",
            element: <SettingsPayFactors />
          },
          {
            key: "audit-logs",
            path: "audit-logs",
            element: <SettingAuditLogs />
          },
          {
            key: "period-management",
            path: "period-management",
            element: <PeriodManagement />
          }
        ]
      },
      {
        path: "help",
        element: <Help />
      },
      {
        path: "profile-settings",
        element: <ProfileSettings />
      },
      {
        path: "about",
        element: <About />
      },
      {
        path: "initial-setup",
        element: <InitialSetup />,
        children: [
          {
            path: "import-sources",
            element: <InitialImportSources />
          },
          {
            path: "login-users",
            element: <InitialSetupUsers />
          },
          {
            path: "roles",
            testKey: "initial-setup-roles",
            element: <InitialSetupRoles />
          },
          {
            path: "email",
            element: <SettingsEmail />
          },
        ]
      }
    ]
  },
  {
    path: "*",
    element: <NotFound />
  }
];

export default router;
